// humanizeError — convert raw DB / HTTP / SDK errors into a plain-English
// { title, body } pair for the Toast UI. Keeps the underlying message in
// the body so support can still see what went wrong; the title is what
// the user reads first.
//
// Add new patterns as they show up — pattern order matters, more specific
// first.

const PATTERNS = [
  // Postgres unique-constraint violation. Common UX cause: trying to
  // create a user with a PIN that already exists, a SKU code that's
  // already used, etc.
  {
    test: /duplicate key value violates unique constraint/i,
    title: 'Already exists',
    body: 'Something with that ID is already in the system. Try a different code, PIN, or number.',
  },
  // Postgres foreign-key violation.
  {
    test: /violates foreign key constraint/i,
    title: 'Linked record not found',
    body: 'A field references something that doesn\'t exist yet — usually a deleted user, supplier, or PO.',
  },
  // Postgres check-constraint violation — specific case: an "amount" or
  // "price" field that requires > 0. Match these BEFORE the generic check
  // pattern so users get the precise hint.
  {
    test: /violates check constraint .*(?:amount|price|cost|total).*positive/i,
    title: 'Amount must be more than $0',
    body: 'The amount, price, or cost on this row is empty or zero. Type a value greater than $0 and try again.',
  },
  // Generic Postgres check-constraint violation (e.g. status='X' not in enum).
  {
    test: /violates check constraint/i,
    title: 'Invalid value',
    body: 'One of the fields is set to a value the database doesn\'t allow. Check the dropdowns.',
  },
  // Postgres NOT NULL violation (saving a row missing a required field).
  {
    test: /null value in column .* violates not-null constraint/i,
    title: 'Missing required field',
    body: 'A required field was left empty. Fill in everything marked with * and try again.',
  },
  // Auth Lambda — invalid PIN.
  {
    test: /Unauthorized\. Valid PIN required/i,
    title: 'Sign-in expired',
    body: 'Your session is no longer valid. Refresh the page and sign in again.',
  },
  // Auth Lambda — admin-only table.
  {
    test: /requires admin role|admin-only/i,
    title: 'Admin only',
    body: 'This action requires admin permission. Ask Mo to enable it for your user.',
  },
  // Auth Lambda — table not whitelisted.
  {
    test: /Table .* is not in the allowed list/i,
    title: 'Not allowed',
    body: 'This table can\'t be written via the secure path yet. Tell Mo which screen you were on.',
  },
  // Locked PO concurrency.
  {
    test: /Conflict|modified by another user/i,
    title: 'Modified by someone else',
    body: 'Someone else changed this record while you were editing. Close and reopen to get the latest version.',
  },
  // Network / fetch failure.
  {
    test: /Failed to fetch|NetworkError|ERR_NETWORK/i,
    title: 'Couldn\'t reach the server',
    body: 'Looks like a connection problem. Check your internet and try again.',
  },
  // S3 storage upload errors.
  {
    test: /storage|upload failed|s3/i,
    title: 'File upload failed',
    body: 'The server didn\'t accept the file. Try a smaller file or a different format (PDF, PNG, JPG).',
  },
  // Generic 5xx
  {
    test: /^5\d\d|Internal (server )?[Ee]rror/,
    title: 'Server error',
    body: 'Something broke on our end. Try again in a moment.',
  },
];

/**
 * Translate any error-shaped value into a Toast-friendly { title, body }.
 *
 * @param {Error|string|unknown} err  Raw error from a catch block, a fetch
 *     response, a Supabase/PostgREST `{ error: { message } }`, or a string.
 * @param {string} [fallbackTitle]    Title to use if no pattern matches.
 *     Defaults to 'Something went wrong'.
 * @returns {{ title: string, body: string }}
 */
export function humanizeError(err, fallbackTitle = 'Something went wrong') {
  const raw = (() => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (err.message) return String(err.message);
    if (err.error?.message) return String(err.error.message);
    try { return JSON.stringify(err); } catch { return String(err); }
  })();

  for (const p of PATTERNS) {
    if (p.test.test(raw)) {
      return { title: p.title, body: p.body };
    }
  }

  // Fall back: surface the raw message — better than swallowing it.
  return { title: fallbackTitle, body: raw || 'No details available.' };
}
